import React from 'react';
import ArtistMemberListModal from './ArtistMemberListModal';
import ArtistGroupNotesPanel from './ArtistGroupNotesPanel';
import ChatComposer from '../../common/chat/composer/ChatComposer';
import ChatImageModal from '../../common/chat/composer/ChatImageModal';
import ChatDragOverlay from '../../common/chat/composer/ChatDragOverlay';
import ChatRetentionEmptyNotice from '../../common/chat/shared/ChatRetentionEmptyNotice';
import GroupStandardMessageRow from '../../common/chat/timeline/GroupStandardMessageRow';
import ChatWorkspaceLayout from '../../common/chat/shared/ChatWorkspaceLayout';
import ChatAreaHeader from '../../common/chat/shared/ChatAreaHeader';
import ChatHeaderIconButton from '../../common/chat/shared/ChatHeaderIconButton';
import ChatHeaderOverflowMenu from '../../common/chat/shared/ChatHeaderOverflowMenu';
import ChatLinksModal from '../../common/chat/assets/ChatLinksModal';
import ChatMediaModal from '../../common/chat/assets/ChatMediaModal';
import { artistGroupChatAssetSource } from '../../../api/chatAssets';
import ChatSidePanelToggleButton from '../../common/chat/sidepanel/ChatSidePanelToggleButton';
import ChatSidePanelHeader from '../../common/chat/sidepanel/ChatSidePanelHeader';
import GroupChatParticipantsPanel from '../../common/chat/members/GroupChatParticipantsPanel';
import ChatProfileModalLayer from '../../common/chat/members/ChatProfileModalLayer';
import ChatAnnouncementBar from '../../common/chat/announcements/ChatAnnouncementBar';
import ChatAnnouncementListModal from '../../common/chat/announcements/ChatAnnouncementListModal';
import ChatMessageReactionBar from '../../common/chat/reactions/ChatMessageReactionBar';
import ChatMessageMenuReactionPicker from '../../common/chat/reactions/ChatMessageMenuReactionPicker';
import ChatMessageReactionDetailModal from '../../common/chat/reactions/ChatMessageReactionDetailModal';
import ChatReplySnippet from '../../common/chat/composer/ChatReplySnippet';
import ChatNoteSystemMessageRow from '../../common/chat/notes/ChatNoteSystemMessageRow';
import AccountAvatarTrigger from '../../common/chat/avatars/AccountAvatarTrigger';
import { ArtistGroupChatGuidanceMessage } from '../../common/chat/timeline/ArtistChatGuidanceSystemMessages';
import { appendVariant } from '../../common/chat/composer/chatAssetUtils';
import { buildGroupChatMessagePresentation } from '../../common/chat/timeline/groupChatMessageUtils';
import { isGroupNoteMessage } from '../../common/chat/notes/groupNoteMessageUtils';
import { createGroupParticipantsPanelProps } from '../../common/chat/members/groupParticipantsPanelConfigUtils';
import {
  ADMIN_PROFILE_IMAGE_PATH,
  API_URL,
  ARTIST_PROFILE_IMAGE_PATH,
  GROUP_CHAT_ICON_IMAGE_PATH,
  EVENT_FLYER_IMAGE_PATH,
  MESSAGE_FILE_PATH,
  MESSAGE_IMAGE_PATH,
  buildAssetUrl,
  fmtDateTime,
} from './artistGroupChatPaneUtils';

export default function ArtistGroupChatPaneView({
  activeAnchorMessageId,
  announcementActionMessageId,
  announcementCount,
  announcements,
  artistUser,
  buildAdminProfileData,
  buildArtistProfileData,
  canAnnouncementMessage,
  canCopyMessage,
  canReactMessage,
  canReplyMessage,
  canUnsendMessage,
  chatDropScopeRef,
  clearMessageLongPress,
  closeImageModal,
  closeMessageMenu,
  closeProfileModal,
  closeReactionDetail,
  displayMessages,
  groupedSidePanelArtists,
  handleAvatarClick,
  handleComposerSent,
  handleCopyMessage,
  handleJumpToReplyMessage,
  handleMentionClick,
  handleMessageContextMenu,
  handleMessageMenuReactionSelect,
  handleMessageTouchStart,
  handleOpenInlinePartialCopy,
  handleSelectAnnouncementMessage,
  handleSelectReplyMessage,
  handleToggleAnnouncementMessage,
  handleUnsendMessage,
  hasMoreMessages,
  imageItems,
  imgModalIndex,
  imgModalOpen,
  inlineCopyMessageId,
  isAnnouncementListOpen,
  isAnnouncedMessage,
  isDragActive,
  isMemberModalOpen,
  isReactionDetailOpen,
  isReactionPending,
  isSidePanelOpen,
  latestAnnouncement,
  mentionCandidates,
  messageMenu,
  messageMenuRef,
  messagesContainerRef,
  members,
  noteCount,
  onMobileBack,
  openAdminProfile,
  openArtistProfile,
  openImageModal,
  openReactionDetail,
  overlayRect,
  participantCount,
  profileTarget,
  reactionDetailGroups,
  reactionDetailLoading,
  readOnly,
  refreshNoteCount,
  renderPartialCopyContent,
  replyDraft,
  setIsAnnouncementListOpen,
  setIsMemberModalOpen,
  setIsSidePanelOpen,
  setReplyDraft,
  setShowNotes,
  showDelayedInitialLoading,
  showInlineLoadingSpinner,
  showMobileBackButton,
  showNextImage,
  showNotes,
  showPrevImage,
  shouldShowEmptyMessages,
  thread,
  threadId,
  threadSummary,
  toggleReaction,
  unpinAnnouncement,
  unsendingMessageId,
}) {
  const [showLinksModal, setShowLinksModal] = React.useState(false);
  const [showMediaModal, setShowMediaModal] = React.useState(false);
  const chatAssetSource = React.useMemo(
    () => (threadId ? artistGroupChatAssetSource(threadId) : null),
    [threadId],
  );
  const normalizedCurrentThreadId = String(threadId || '');
  const normalizedLoadedThreadId = String(thread?.thread_id || '');
  const headerThread =
    normalizedLoadedThreadId && normalizedLoadedThreadId === normalizedCurrentThreadId
      ? thread
      : threadSummary || thread || null;
  const headerTitle = headerThread?.title || 'グループチャット';
  const headerIconUrl = appendVariant(
    buildAssetUrl(
      headerThread?.icon_file ? GROUP_CHAT_ICON_IMAGE_PATH : EVENT_FLYER_IMAGE_PATH,
      headerThread?.icon_file || headerThread?.linked_event_flyer_front_url || ''
    ),
    'proxy'
  );
  const composerReadOnly = readOnly || thread?.status !== 'open';
  const composerPlaceholder =
    thread?.status === 'open' ? 'メッセージを入力…' : 'クローズ中のため送信できません';
  const composerBlockedMessage =
    thread?.status === 'open'
      ? '現在メンテナンス中のため送信できません。'
      : 'クローズ中のため送信できません。';

  return (
    <section className="artist-group-chat-pane-root">
      <ChatWorkspaceLayout
        className={`artist-group-chat-pane__workspace${isSidePanelOpen ? '' : ' is-side-panel-collapsed'}`}
      >
        <div className="artist-message-chat-container artist-group-chat-pane__main chat-area chat-dnd-scope" ref={chatDropScopeRef}>
          <ChatAreaHeader
            className="artist-chat-main-header"
            mainClassName="artist-chat-main-header__main"
            showBackButton={showMobileBackButton}
            onBack={() => {
              if (typeof onMobileBack === 'function') onMobileBack();
            }}
            backButtonClassName="chat-header-back-btn"
            backAriaLabel="チャット一覧に戻る"
            backTitle="チャット一覧に戻る"
            titleContent={(
              <>
                <div
                  className="artist-chat-main-header__avatar artist-group-chat-pane__header-avatar"
                  style={headerIconUrl ? { backgroundImage: `url(${headerIconUrl})` } : undefined}
                  aria-hidden="true"
                >
                  {!headerIconUrl && (String(headerTitle || 'G').trim().charAt(0) || 'G')}
                </div>
                <div className="artist-chat-main-header__text">
                  <h3>{headerTitle}</h3>
                  {headerThread?.linked_event_name ? <small>{headerThread.linked_event_name}</small> : null}
                </div>
              </>
            )}
            actionsClassName="artist-group-chat-pane__header-actions chat-header-action-group"
            actions={(
              <>
                <ChatHeaderIconButton
                  className="artist-group-chat-pane__memberButton"
                  onClick={() => setShowNotes((current) => !current)}
                  aria-label={showNotes ? 'チャットに戻る' : 'ノート'}
                  title={showNotes ? 'チャットに戻る' : 'ノート'}
                  disabled={!threadId}
                  iconClassName={`fa-solid ${showNotes ? 'fa-comment' : 'fa-note-sticky'}`}
                  badgeCount={showNotes ? 0 : noteCount}
                />
                <ChatHeaderIconButton
                  className="artist-group-chat-pane__memberButton"
                  onClick={() => setIsMemberModalOpen(true)}
                  aria-label="メンバーリスト"
                  title="メンバーリスト"
                  disabled={!threadId}
                  iconClassName="fa-solid fa-people-group"
                  badgeCount={participantCount}
                  showZeroBadge
                />
                <ChatHeaderOverflowMenu
                  items={[
                    { key: 'links', icon: 'fa-link', label: 'リンク', onClick: () => setShowLinksModal(true), hidden: !threadId },
                    { key: 'media', icon: 'fa-photo-film', label: '写真・ファイル', onClick: () => setShowMediaModal(true), hidden: !threadId },
                  ]}
                />
              </>
            )}
          />

          {!showNotes ? (
            <ChatAnnouncementBar
              announcement={latestAnnouncement}
              count={announcementCount}
              onOpenList={() => setIsAnnouncementListOpen(true)}
            />
          ) : null}

          {showNotes ? (
            <ArtistGroupNotesPanel
              threadId={threadId}
              onModeChange={() => {}}
              onNotesChanged={refreshNoteCount}
            />
          ) : (
            <>
              <div className="artist-group-chat-pane__messages chat-message-stack" ref={messagesContainerRef}>
                <ChatDragOverlay
                  show={isDragActive}
                  rect={overlayRect}
                  title="ここにドロップで送信"
                  sub="画像（最大5枚）またはファイル1件（100MBまで）"
                />
                {showDelayedInitialLoading ? (
                  <div className="chat-loading-overlay" aria-label="読み込み中">
                    <div className="chat-spinner" aria-hidden="true"></div>
                  </div>
                ) : null}
                {displayMessages.map((message) => {
                  const formattedTime = fmtDateTime(message.created_at);
                  if (isGroupNoteMessage(message)) {
                    const presentation = buildGroupChatMessagePresentation({
                      message,
                      viewerRole: 'artist',
                      currentArtistId: artistUser?.userId,
                      apiUrl: API_URL,
                      paths: {
                        adminProfilePath: ADMIN_PROFILE_IMAGE_PATH,
                        artistProfilePath: ARTIST_PROFILE_IMAGE_PATH,
                        messageImagePath: MESSAGE_IMAGE_PATH,
                        messageFilePath: MESSAGE_FILE_PATH,
                      },
                      withDownloadQuery: false,
                    });
                    return (
                      <ChatNoteSystemMessageRow
                        key={message.id}
                        message={message}
                        variant="plain"
                        avatarNode={(
                          <AccountAvatarTrigger
                            className="message-avatar"
                            imageUrl={presentation.avatarUrl}
                            style={presentation.avatarUrl ? undefined : presentation.avatarStyle}
                            fallback={<span>{presentation.avatarInitial}</span>}
                          />
                        )}
                        senderName={presentation.senderName}
                        authorIsAdmin={presentation.isAdminSender}
                        bubbleClassName="im-bubble admin-message system-message"
                        timeLabel={formattedTime}
                        activeAnchorMessageId={activeAnchorMessageId}
                        onOpenNote={(noteId) => {
                          setShowNotes(true);
                          if (noteId) {
                            window.setTimeout(() => {
                              window.dispatchEvent(
                                new CustomEvent('artist-group-notes-open-detail', { detail: { noteId } })
                              );
                            }, 0);
                          }
                        }}
                      />
                    );
                  }
                  const presentation = buildGroupChatMessagePresentation({
                    message,
                    viewerRole: 'artist',
                    currentArtistId: artistUser?.userId,
                    apiUrl: API_URL,
                    paths: {
                      adminProfilePath: ADMIN_PROFILE_IMAGE_PATH,
                      artistProfilePath: ARTIST_PROFILE_IMAGE_PATH,
                      messageImagePath: MESSAGE_IMAGE_PATH,
                      messageFilePath: MESSAGE_FILE_PATH,
                    },
                    withDownloadQuery: false,
                  });
                  const isSelf = presentation.isSelf;
                  const normalizedMessageId = String(message?.id || '').trim();
                  const hasCopyFocus = Boolean(
                    normalizedMessageId &&
                    inlineCopyMessageId &&
                    inlineCopyMessageId === normalizedMessageId
                  );
                  const shouldDimRow = Boolean(
                    inlineCopyMessageId &&
                    normalizedMessageId &&
                    inlineCopyMessageId !== normalizedMessageId
                  );
                  return (
                    <GroupStandardMessageRow
                      key={message.id}
                      message={message}
                      presentation={presentation}
                      isSelf={isSelf}
                      variant="plain"
                      selfBubbleClass="im-bubble artist-message"
                      otherBubbleClass="im-bubble admin-message"
                      bubbleClassName={hasCopyFocus ? 'chat-message-row__bubbleWrap--copyActive' : ''}
                      rowClassName={
                        hasCopyFocus
                          ? 'chat-message-row--copyActive'
                          : shouldDimRow
                            ? 'chat-message-row--copyDimmed'
                            : ''
                      }
                      timeLabel={formattedTime}
                      activeAnchorMessageId={activeAnchorMessageId}
                      anchorClassName="is-anchor-hit"
                      avatarClassName="message-avatar"
                      avatarClickableClassName="message-avatar--clickable"
                      avatarFallbackClassName="artist-group-chat-pane__avatar-fallback"
                      avatarAlt=""
                      onAvatarClick={!isSelf ? () => handleAvatarClick(message) : undefined}
                      onContextMenu={inlineCopyMessageId ? undefined : (event) => handleMessageContextMenu(event, message)}
                      onTouchStart={inlineCopyMessageId ? undefined : (event) => handleMessageTouchStart(event, message)}
                      onTouchEnd={inlineCopyMessageId ? undefined : clearMessageLongPress}
                      onTouchCancel={inlineCopyMessageId ? undefined : clearMessageLongPress}
                      onTouchMove={inlineCopyMessageId ? undefined : clearMessageLongPress}
                      onImageClick={() => openImageModal(message.id)}
                      onMentionClick={handleMentionClick}
                      fileVariant="rich"
                      replyHeaderContent={message.reply ? (
                        <ChatReplySnippet
                          reply={message.reply}
                          onClick={() => handleJumpToReplyMessage(message.reply)}
                        />
                      ) : null}
                      wrapContent={(contentNode, msg) => renderPartialCopyContent(msg, contentNode, isSelf)}
                      sideActionContent={
                        !inlineCopyMessageId &&
                        !isSelf && canReactMessage(message) ? (
                          <ChatMessageReactionBar
                            messageId={message.id}
                            reactions={message.reactions}
                            canReact
                            showReactionChips={false}
                            onToggleReaction={toggleReaction}
                            onOpenDetail={openReactionDetail}
                            isPending={isReactionPending}
                            compact
                          />
                        ) : null
                      }
                      metaEndContent={
                        Array.isArray(message.reactions) &&
                        message.reactions.length > 0 ? (
                          <ChatMessageReactionBar
                            messageId={message.id}
                            reactions={message.reactions}
                            canReact={false}
                            onOpenDetail={openReactionDetail}
                            isPending={isReactionPending}
                            showAddButton={false}
                          />
                        ) : null
                      }
                    />
                  );
                })}
                {!hasMoreMessages && !showInlineLoadingSpinner && threadId ? (
                  <ArtistGroupChatGuidanceMessage />
                ) : null}
                {showInlineLoadingSpinner ? (
                  <div className="chat-paging-loading" aria-label="追加読み込み中">
                    <div className="chat-spinner chat-spinner--small" aria-hidden="true"></div>
                  </div>
                ) : null}
                {shouldShowEmptyMessages ? (
                  <ChatRetentionEmptyNotice />
                ) : null}
              </div>

              <ChatImageModal
                isOpen={imgModalOpen && imageItems.length > 0}
                src={imageItems[imgModalIndex]?.fullSrc}
                downloadItems={imageItems}
                currentIndex={imgModalIndex}
                onClose={closeImageModal}
                onPrev={showPrevImage}
                onNext={showNextImage}
                showNav={imageItems.length > 1}
              />

              {messageMenu ? (
                <div className="artist-group-chat-pane__messageMenuOverlay" onClick={closeMessageMenu}>
                  <div
                    ref={messageMenuRef}
                    className="artist-group-chat-pane__messageMenu"
                    style={{ left: messageMenu.x, top: messageMenu.y }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {canReactMessage(messageMenu.message) ? (
                      <ChatMessageMenuReactionPicker
                        messageId={messageMenu.messageId}
                        reactions={messageMenu.message?.reactions}
                        onSelectReaction={handleMessageMenuReactionSelect}
                        isPending={isReactionPending}
                      />
                    ) : null}
                    {canReplyMessage(messageMenu.message) ? (
                      <button
                        type="button"
                        className="artist-group-chat-pane__messageMenuItem"
                        onClick={handleSelectReplyMessage}
                      >
                        <i className="fa-solid fa-reply" aria-hidden="true" />
                        <span>返信する</span>
                      </button>
                    ) : null}
                    {canCopyMessage(messageMenu.message) ? (
                      <button
                        type="button"
                        className="artist-group-chat-pane__messageMenuItem"
                        onClick={handleCopyMessage}
                      >
                        <i className="fa-solid fa-copy" aria-hidden="true" />
                        <span>コピー</span>
                      </button>
                    ) : null}
                    {canCopyMessage(messageMenu.message) ? (
                      <button
                        type="button"
                        className="artist-group-chat-pane__messageMenuItem"
                        onClick={handleOpenInlinePartialCopy}
                      >
                        <i className="fa-solid fa-i-cursor" aria-hidden="true" />
                        <span>一部をコピー</span>
                      </button>
                    ) : null}
                    {canAnnouncementMessage(messageMenu.message) ? (
                      <button
                        type="button"
                        className="artist-group-chat-pane__messageMenuItem"
                        onClick={handleToggleAnnouncementMessage}
                        disabled={Boolean(announcementActionMessageId)}
                      >
                        <i
                          className={`fa-solid ${
                            announcementActionMessageId === String(messageMenu.messageId || '')
                              ? 'fa-spinner fa-spin'
                              : isAnnouncedMessage(messageMenu.messageId)
                                ? 'fa-bullhorn-slash'
                                : 'fa-bullhorn'
                          }`}
                          aria-hidden="true"
                        />
                        <span>
                          {announcementActionMessageId === String(messageMenu.messageId || '')
                            ? '処理中…'
                            : isAnnouncedMessage(messageMenu.messageId)
                              ? 'アナウンス解除'
                              : 'アナウンスに設定'}
                        </span>
                      </button>
                    ) : null}
                    {canUnsendMessage(messageMenu.message) ? (
                      <button
                        type="button"
                        className="artist-group-chat-pane__messageMenuItem artist-group-chat-pane__messageMenuItem--danger"
                        onClick={handleUnsendMessage}
                        disabled={Boolean(unsendingMessageId)}
                      >
                        {unsendingMessageId ? '処理中…' : '送信取り消し'}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <ChatComposer
                mode="artist"
                scope="group"
                groupThreadId={threadId}
                replyDraft={replyDraft}
                onReplyCancel={() => setReplyDraft(null)}
                readOnly={composerReadOnly}
                blockedMessage={composerBlockedMessage}
                placeholderText={composerPlaceholder}
                onSent={handleComposerSent}
                mentionCandidates={mentionCandidates}
              />
            </>
          )}
        </div>

        <aside
          className={`chat-side-panel side-panel artist-chat-side-panel artist-group-chat-pane__side${
            isSidePanelOpen ? '' : ' chat-side-panel--collapsed'
          }`}
        >
          {isSidePanelOpen ? (
            <>
              <ChatSidePanelHeader
                icon="fa-comments"
                label="チャット情報"
                onCollapse={() => setIsSidePanelOpen(false)}
              />
              <GroupChatParticipantsPanel
                {...createGroupParticipantsPanelProps({
                  assignees: members.assignees,
                  groupedArtists: groupedSidePanelArtists,
                  onSelectAssignee: (admin) => {
                    const payload = buildAdminProfileData(admin);
                    openAdminProfile(payload);
                  },
                  onSelectArtist: (artist) => {
                    const payload = buildArtistProfileData(artist);
                    openArtistProfile(payload);
                  },
                  buildAssetUrl,
                  appendVariant,
                  adminProfilePath: ADMIN_PROFILE_IMAGE_PATH,
                  artistProfilePath: ARTIST_PROFILE_IMAGE_PATH,
                  assigneeEmptyText: '担当管理者は未設定です。',
                  artistEmptyText: '参加アーティストはいません。',
                  assigneeMetaFallback: '',
                  artistNameFallback: 'アーティスト',
                })}
                assigneeAvatarClassName="artist-group-chat-pane__sideAvatar"
                artistAvatarClassName="artist-group-chat-pane__sideAvatar"
              />
            </>
          ) : (
            <div className="chat-side-panel__collapsed-rail">
              <ChatSidePanelToggleButton
                isOpen={false}
                onToggle={() => setIsSidePanelOpen(true)}
                label="チャット情報"
                iconOnly
                className="chat-side-panel__collapsed-toggle"
              />
            </div>
          )}
        </aside>
      </ChatWorkspaceLayout>

      <ArtistMemberListModal
        isOpen={isMemberModalOpen}
        onClose={() => setIsMemberModalOpen(false)}
        groupThreadId={threadId}
        groupThread={thread || threadSummary || null}
      />

      <ChatLinksModal
        isOpen={showLinksModal}
        loadLinks={chatAssetSource?.loadLinks}
        onClose={() => setShowLinksModal(false)}
      />
      <ChatMediaModal
        isOpen={showMediaModal}
        loadMedia={chatAssetSource?.loadMedia}
        downloadZip={chatAssetSource?.downloadZip}
        onClose={() => setShowMediaModal(false)}
      />

      <ChatProfileModalLayer
        profileTarget={profileTarget}
        onClose={closeProfileModal}
        variant="artist"
        allowArtistLink={false}
      />
      <ChatAnnouncementListModal
        open={isAnnouncementListOpen}
        announcements={announcements}
        onClose={() => setIsAnnouncementListOpen(false)}
        onRemove={unpinAnnouncement}
        removingMessageId={announcementActionMessageId}
        onSelect={handleSelectAnnouncementMessage}
      />
      <ChatMessageReactionDetailModal
        open={isReactionDetailOpen}
        loading={reactionDetailLoading}
        groups={reactionDetailGroups}
        onClose={closeReactionDetail}
      />
    </section>
  );
}
